import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Chip,
  Button,
  Grid,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplyIcon from '@mui/icons-material/Reply';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import facturaVentaService, { FacturaVenta } from '../../services/facturaVentaService';
import sucVentaService from '../../services/sucVentaService';
import remitoVentaService, { RemitoVentaCreateDto } from '../../services/remitoVentaService';
import mercadoPagoService from '../../services/mercadoPagoService';
import PDFViewer from '../../components/shared/PDFViewer';
import QRPaymentDialog from '../../components/shared/QRPaymentDialog';
import api from '../../services/api';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../utils/apiError';

const Facturas: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  // Obtener el primer y último día del mes actual
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  const [facturas, setFacturas] = useState<FacturaVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [fechaDesde, setFechaDesde] = useState<string>(() => {
    const desde = searchParams.get('fechaDesde');
    if (desde && /^\d{4}-\d{2}-\d{2}$/.test(desde)) return desde;
    const fecha = getFirstDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState<string>(() => {
    const hasta = searchParams.get('fechaHasta');
    if (hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta)) return hasta;
    const fecha = getLastDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedFactura, setSelectedFactura] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [selectedFacturaForPayment, setSelectedFacturaForPayment] = useState<number | null>(null);
  const [obteniendoCae, setObteniendoCae] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);
  const [sucVentaMap, setSucVentaMap] = useState<Record<number, { generaRemito: string; nroSucRemito?: number }>>({});
  const [generandoRemito, setGenerandoRemito] = useState<number | null>(null);

  const fetchFacturas = async (pageNumber: number, pageSize: number, search: string, desde: string, hasta: string) => {
    try {
      setLoading(true);
      // Asegurar que pageSize esté entre 1 y 20 (límite del backend)
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      const response = await facturaVentaService.getFacturasVenta({
        pageNumber: pageNumber,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde ? new Date(desde) : undefined,
        fechaHasta: hasta ? new Date(hasta) : undefined
      });

      setFacturas(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: any) {
      console.error('Error al cargar facturas:', error);
      const errorMessage = getApiErrorMessage(error, 'Error al cargar las facturas');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 5000
      });
      setFacturas([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // Sincronizar fechas desde URL cuando cambian los searchParams (ej. al llegar desde dashboard)
  useEffect(() => {
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    if (desde && /^\d{4}-\d{2}-\d{2}$/.test(desde)) setFechaDesde(desde);
    if (hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta)) setFechaHasta(hasta);
    setPage(0);
  }, [searchParams]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchFacturas(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta]);

  useEffect(() => {
    const loadSucVentas = async () => {
      try {
        // API SucVenta limita PageSize a 20; cargar todas las páginas para el mapa de generaRemito
        const allItems: Array<{ nroSuc: number; generaRemito?: string; nroSucRemito?: number }> = [];
        let page = 1;
        const pageSize = 20;
        let hasMore = true;
        while (hasMore) {
          const res = await sucVentaService.getSucVentas({
            pageNumber: page,
            pageSize,
            nroTipoFormulario: '1',
          });
          allItems.push(...res.items);
          hasMore = res.items.length === pageSize && page * pageSize < (res.totalCount ?? 0);
          page++;
        }
        const map: Record<number, { generaRemito: string; nroSucRemito?: number }> = {};
        allItems.forEach((sv) => {
          map[sv.nroSuc] = {
            generaRemito: sv.generaRemito ?? 'N',
            nroSucRemito: sv.nroSucRemito,
          };
        });
        setSucVentaMap(map);
      } catch (err) {
        console.warn('No se pudo cargar SucVentas para botón Generar remito:', err);
      }
    };
    loadSucVentas();
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      setPdfLoading(nroFormulario);
      const response = await api.get(
        `/api/FacturasVenta/${nroFormulario}/pdf`,
        {
          responseType: 'blob',
        }
      );
      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedFactura(nroFormulario);
    } catch (error: any) {
      console.error('Error al generar el PDF de la factura:', error);
      const errorMessage = getErrorMessage(error, 'Error al generar el PDF de la factura');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 5000
      });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedFactura(null);
    setPdfFileName(null);
  };

  const getErrorMessage = (error: any, defaultMessage: string) => {
    if (error?.response?.data) {
      let data = error.response.data;
      
      // Si viene como string, intentar parsearlo como JSON
      if (typeof data === 'string') {
        try {
          // Si es un string JSON, parsearlo
          if (data.trim().startsWith('{') || data.trim().startsWith('[')) {
            data = JSON.parse(data);
          } else {
            // Si es un string simple, devolverlo directamente
            return data;
          }
        } catch {
          // Si no es JSON válido, devolver el string tal cual
          return data;
        }
      }
      
      // Si ahora es un objeto, buscar el mensaje en diferentes propiedades
      if (typeof data === 'object' && data !== null) {
        // Buscar en orden de prioridad: mensaje (español), message (inglés), error, detail, title
        const mensaje = 
          data.mensaje || 
          data.message || 
          data.error || 
          data.detail || 
          data.title ||
          data.Mensaje ||
          data.Message;
        
        if (mensaje) {
          return mensaje;
        }
        
        // Si tiene una propiedad con el mensaje anidado, intentar extraerlo
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          return data.errors[0].message || data.errors[0].mensaje || data.errors[0];
        }
      }
    }
    
    // Si no se encontró un mensaje específico, usar el mensaje del error o el default
    return error?.message || defaultMessage;
  };

  const handleGenerarLinkPago = async (nroFormulario: number) => {
    try {
      setQrLoading(true);
      setSelectedFacturaForPayment(nroFormulario);
      setQrDialogOpen(true);
      setQrCode(null);
      setPaymentLink(null);

      const response = await mercadoPagoService.getPaymentLink(nroFormulario);
      setQrCode(response.qrCode || response.paymentLink);
      setPaymentLink(response.paymentLink);
    } catch (error: any) {
      console.error('Error al generar link de pago:', error);
      const errorMessage = getErrorMessage(error, 'Error al generar el link de pago');
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 3000
      });
      setQrDialogOpen(false);
    } finally {
      setQrLoading(false);
    }
  };

  const handleCloseQRDialog = () => {
    setQrDialogOpen(false);
    setQrCode(null);
    setPaymentLink(null);
    setSelectedFacturaForPayment(null);
  };

  const handleObtenerCae = async (nroFormulario: number) => {
    try {
      setObteniendoCae(nroFormulario);
      const facturaActualizada = await facturaVentaService.obtenerCaeArgentina(nroFormulario);
      
      // Actualizar la factura en la lista con todos los campos relevantes
      setFacturas(prevFacturas => 
        prevFacturas.map(f => 
          f.nroFormulario === nroFormulario 
            ? { 
                ...f, 
                nroComprobante: facturaActualizada.nroComprobante,
                caeNro: facturaActualizada.caeNro, 
                caeVencimiento: facturaActualizada.caeVencimiento,
                caeResultado: facturaActualizada.caeResultado,
                caeObs: facturaActualizada.caeObs
              }
            : f
        )
      );
      
      enqueueSnackbar('CAE obtenido exitosamente', { variant: 'success' });
    } catch (error: any) {
      console.error('Error al obtener CAE:', error);
      const errorMessage = getErrorMessage(error, 'Error al obtener el CAE');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 8000
      });
    } finally {
      setObteniendoCae(null);
    }
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '-';
    try {
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      return date.toLocaleDateString('es-AR');
    } catch {
      return typeof dateString === 'string' ? dateString : '-';
    }
  };

  const formatComprobante = (factura: FacturaVenta) => {
    const { letra, nroSuc, nroComprobante } = factura;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) {
        return ''.padStart(length, '0');
      }
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
      }
      return String(value).padStart(length, '0');
    };

    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();

    if (!letraNormalizada) {
      return '-';
    }

    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  const esFacturaNoCategorizada = (factura: FacturaVenta) => {
    const tipoIva = (factura.tipoIva ?? factura.tipoCliente ?? '').toString().trim().toUpperCase();
    return tipoIva === 'N';
  };

  const handleFacturarFacturaNoCategorizada = (nroFormulario: number) => {
    if (!nroFormulario) return;
    navigate(`/ventas/facturas/nuevo?facturaId=${nroFormulario}`);
  };

  const handleAnular = async (facturaId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm('¿Está seguro que desea anular esta factura?')) {
      try {
        await facturaVentaService.anularFactura(facturaId);
        enqueueSnackbar('Factura anulada correctamente', { variant: 'success' });
        fetchFacturas(page, rowsPerPage, searchTerm, fechaDesde, fechaHasta);
      } catch (error: any) {
        const message = error?.response?.data?.message || error?.response?.data || 'Error al anular la factura';
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
      }
    }
  };

  const puedeGenerarRemito = (factura: FacturaVenta) => {
    if (factura.nroFormularioRemito != null && factura.nroFormularioRemito > 0) return false;
    const nroSuc = factura.nroSuc ?? 0;
    const sv = sucVentaMap[nroSuc];
    return sv?.generaRemito === 'S';
  };

  const handleGenerarRemito = async (nroFormulario: number) => {
    try {
      setGenerandoRemito(nroFormulario);
      const factura = await facturaVentaService.getFacturaVenta(nroFormulario);
      const nroSuc = factura.nroSuc ?? 0;
      const sucVenta = await sucVentaService.getSucVenta(nroSuc, '1');
      if (sucVenta.generaRemito !== 'S') {
        enqueueSnackbar('Esta sucursal no permite generar remitos desde facturas', { variant: 'error' });
        return;
      }
      if (!factura.detalles || factura.detalles.length === 0) {
        enqueueSnackbar('La factura no tiene items para generar el remito', { variant: 'error' });
        return;
      }
      if (!factura.nroCliente || factura.nroCliente <= 0) {
        enqueueSnackbar('La factura debe tener un cliente asignado', { variant: 'error' });
        return;
      }
      const ahora = new Date();
      const payload: RemitoVentaCreateDto = {
        formulario: {
          nroCliente: factura.nroCliente,
          nombre: factura.nombre,
          cuit: factura.cuit,
          nroLista: factura.nroLista,
          nroSuc: sucVenta.nroSucRemito ?? factura.nroSuc ?? 0,
          descripcion: factura.descripcion ?? `Remito desde factura ${nroFormulario}`,
          nroFormularioFactura: nroFormulario,
          fechaEmision: ahora.toISOString(),
          horaEmision: ahora.toISOString(),
          fechaVencim: ahora.toISOString(),
          nroUbicacionOrigen: (factura as any).nroUbicacionOrigen ?? undefined,
          porcDesc: factura.porcDesc ?? 0,
          porcDesc2: factura.porcDesc2 ?? 0,
          porcDesc3: factura.porcDesc3 ?? 0,
        },
        items: factura.detalles.map((d) => ({
          codigoArticulo: String(d.codigoArticulo ?? '').trim(),
          descripcion: d.descripcionArticulo ?? '',
          cantidad: Number(d.cantidad ?? 0),
          precioUnitario: Number(d.precioUnitario ?? 0),
          porcDesc: d.porcDesc ?? 0,
          notas: d.notas ?? '',
        })),
      };
      const remitoCreado = await remitoVentaService.createRemitoVenta(payload);
      enqueueSnackbar(`Remito generado correctamente (Nro: ${remitoCreado.nroFormulario})`, { variant: 'success' });
      navigate('/ventas/remitos');
    } catch (error: any) {
      const msg = getApiErrorMessage(error, 'Error al generar el remito');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 5000 });
    } finally {
      setGenerandoRemito(null);
    }
  };

  // Función para obtener el chip de estado según el CAE y el tipo de IVA.
  const getEstadoChip = (factura: FacturaVenta) => {
    const tipoIva = (factura.tipoIva ?? factura.tipoCliente ?? '').toString().trim().toUpperCase();
    if (tipoIva === 'N') {
      return {
        label: 'Interno',
        bgColor: '#9e9e9e',
        textColor: '#ffffff'
      };
    }
    if (factura.caeNro) {
      return {
        label: 'Con CAE',
        bgColor: '#4caf50',
        textColor: '#ffffff'
      };
    }
    return {
      label: 'Sin CAE',
      bgColor: '#ff9800',
      textColor: '#ffffff'
    };
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título y botón */}
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="center" 
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Facturas de Venta
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/ventas/facturas/nuevo')}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                }
              }}
            >
              Nueva Factura
            </Button>
          </Stack>

          {/* Campo de búsqueda y filtros */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por número, cliente, nombre o CUIT..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {facturas.map((factura) => {
                const estadoChip = getEstadoChip(factura);
                const comprobante = formatComprobante(factura);
                return (
                  <Card 
                    key={factura.nroFormulario} 
                    sx={{ 
                      width: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Factura #{factura.nroFormulario}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {factura.nombre?.trim() || '-'}
                            </Typography>
                          </Box>
                          {estadoChip.label ? (
                            <Chip
                              label={estadoChip.label}
                              size="small"
                              sx={{
                                bgcolor: estadoChip.bgColor,
                                color: estadoChip.textColor,
                                fontWeight: 500,
                                borderRadius: 1.5,
                              }}
                            />
                          ) : null}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fecha:</strong> {formatDate(factura.fechaEmision)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cliente:</strong> {factura.nroCliente || '-'}
                        </Typography>
                        {factura.cuit && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CUIT:</strong> {factura.cuit.trim()}
                          </Typography>
                        )}
                        {comprobante !== '-' && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Comprobante:</strong> {comprobante}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          <strong>Importe Total:</strong> {formatCurrency(factura.importeTotal)}
                        </Typography>
                        {factura.usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {factura.usuario}
                          </Typography>
                        )}
                        {factura.caeNro && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CAE:</strong> {factura.caeNro}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          {esFacturaNoCategorizada(factura) && (
                            <Tooltip title="Facturar con CAE">
                              <IconButton
                                onClick={() => handleFacturarFacturaNoCategorizada(factura.nroFormulario)}
                                size="small"
                                sx={{
                                  bgcolor: '#4caf50',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#45a049' }
                                }}
                              >
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Visualizar">
                            <IconButton
                              onClick={() => navigate(`/ventas/facturas/${factura.nroFormulario}/ver`)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1976d2' }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(user?.roles.includes('SUPERADMIN') || user?.roles.includes('BACKDOOR') || user?.roles.includes('SOPORTE-IT')) && (
                            <Tooltip title="Editar (Solo para Superadmin, Backdoor o Soporte IT)">
                              <IconButton
                                onClick={() => navigate(`/ventas/facturas/${factura.nroFormulario}/editar`)}
                                size="small"
                                sx={{
                                  bgcolor: '#9c27b0',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#7b1fa2' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Devolver (Nota de crédito)">
                            <IconButton
                              onClick={() => navigate(`/ventas/notas-credito/nuevo?facturaId=${factura.nroFormulario}`)}
                              size="small"
                              sx={{
                                bgcolor: '#ff9800',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#f57c00' }
                              }}
                            >
                              <ReplyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {puedeGenerarRemito(factura) && (
                            <Tooltip title="Generar remito">
                              <IconButton
                                onClick={() => handleGenerarRemito(factura.nroFormulario)}
                                size="small"
                                disabled={generandoRemito === factura.nroFormulario}
                                sx={{
                                  bgcolor: '#795548',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#6d4c41' },
                                  '&:disabled': { bgcolor: '#ccc' }
                                }}
                              >
                                {generandoRemito === factura.nroFormulario ? (
                                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                                ) : (
                                  <LocalShippingIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          {!factura.caeNro && (
                            <>
                              <Tooltip title="Obtener CAE de Argentina">
                                <IconButton
                                  onClick={() => handleObtenerCae(factura.nroFormulario)}
                                  size="small"
                                  disabled={obteniendoCae === factura.nroFormulario}
                                  sx={{
                                    bgcolor: '#2196f3',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    '&:hover': { bgcolor: '#1976d2' },
                                    '&:disabled': { bgcolor: '#ccc' }
                                  }}
                                >
                                  {obteniendoCae === factura.nroFormulario ? (
                                    <CircularProgress size={16} sx={{ color: '#fff' }} />
                                  ) : (
                                    <CloudDownloadIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Anular factura">
                                <IconButton
                                  onClick={(e) => handleAnular(factura.nroFormulario, e)}
                                  size="small"
                                  sx={{
                                    bgcolor: '#f44336',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    '&:hover': { bgcolor: '#d32f2f' }
                                  }}
                                >
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="Imprimir PDF">
                            <IconButton
                              onClick={() => handleImprimirPDF(factura.nroFormulario)}
                              size="small"
                              disabled={pdfLoading === factura.nroFormulario}
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1976d2' },
                                '&:disabled': { bgcolor: '#ccc' }
                              }}
                            >
                              {pdfLoading === factura.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <PrintIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Generar link de pago Mercado Pago">
                            <IconButton
                              onClick={() => handleGenerarLinkPago(factura.nroFormulario)}
                              size="small"
                              sx={{
                                bgcolor: '#4caf50',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#388e3c' }
                              }}
                            >
                              <QrCodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && facturas.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay facturas para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                maxWidth: '100%',
                overflowX: 'auto',
                '& .MuiTable-root': {
                  width: '100%'
                }
              }}
            >
          <Table 
            size={isTablet ? 'small' : 'medium'} 
            sx={{ 
              minWidth: 900,
              '& .MuiTableCell-root': {
                fontSize: { xs: '0.75rem', md: '0.875rem', lg: '0.875rem' },
                padding: { xs: '8px 4px', sm: '10px 6px', md: '12px 8px' }
              }
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 100 }}>Nro. Factura</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' }, minWidth: 90 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>Comprobante</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' }, minWidth: 70 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 150, maxWidth: 200 }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>CUIT</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 120 }} align="right">Importe Total</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' }, minWidth: 80 }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' }, minWidth: 100 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 120 }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facturas.map((factura) => {
                const estadoChip = getEstadoChip(factura);
                return (
                  <TableRow 
                    key={factura.nroFormulario} 
                    sx={{
                      '&:hover': {
                        bgcolor: '#f9f9f9',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{factura.nroFormulario}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(factura.fechaEmision)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      {formatComprobante(factura)}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{factura.nroCliente || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {factura.nombre?.trim() || '-'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{factura.cuit?.trim() || '-'}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1976d2' }} align="right">{formatCurrency(factura.importeTotal)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>{factura.usuario || '-'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                      {estadoChip.label ? (
                        <Chip
                          label={estadoChip.label}
                          size="small"
                          sx={{
                            bgcolor: estadoChip.bgColor,
                            color: estadoChip.textColor,
                            fontWeight: 500,
                            borderRadius: 1.5,
                            minWidth: 90,
                          }}
                        />
                      ) : ''}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="nowrap">
                        {esFacturaNoCategorizada(factura) && (
                          <Tooltip title="Facturar con CAE">
                            <IconButton
                              onClick={() => handleFacturarFacturaNoCategorizada(factura.nroFormulario)}
                              size="small"
                              sx={{
                                bgcolor: '#4caf50',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#45a049',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Visualizar">
                          <IconButton
                            onClick={() => navigate(`/ventas/facturas/${factura.nroFormulario}/ver`)}
                            size="small"
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              width: 32,
                              height: 32,
                              '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                              transition: 'all 0.2s'
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {(user?.roles.includes('SUPERADMIN') || user?.roles.includes('BACKDOOR') || user?.roles.includes('SOPORTE-IT')) && (
                          <Tooltip title="Editar (Solo para Superadmin, Backdoor o Soporte IT)">
                            <IconButton
                              onClick={() => navigate(`/ventas/facturas/${factura.nroFormulario}/editar`)}
                              size="small"
                              sx={{
                                bgcolor: '#9c27b0',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { bgcolor: '#7b1fa2', transform: 'scale(1.1)' },
                                transition: 'all 0.2s'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Devolver (Nota de crédito)">
                          <IconButton
                            onClick={() => navigate(`/ventas/notas-credito/nuevo?facturaId=${factura.nroFormulario}`)}
                            size="small"
                            sx={{
                              bgcolor: '#ff9800',
                              color: '#fff',
                              borderRadius: 1.5,
                              width: 32,
                              height: 32,
                              '&:hover': { bgcolor: '#f57c00', transform: 'scale(1.1)' },
                              transition: 'all 0.2s'
                            }}
                          >
                            <ReplyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {puedeGenerarRemito(factura) && (
                          <Tooltip title="Generar remito">
                            <IconButton
                              onClick={() => handleGenerarRemito(factura.nroFormulario)}
                              size="small"
                              disabled={generandoRemito === factura.nroFormulario}
                              sx={{
                                bgcolor: '#795548',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { bgcolor: '#6d4c41', transform: 'scale(1.1)' },
                                '&:disabled': { bgcolor: '#ccc' },
                                transition: 'all 0.2s'
                              }}
                            >
                              {generandoRemito === factura.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <LocalShippingIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {!factura.caeNro && (
                          <>
                            <Tooltip title="Obtener CAE de Argentina">
                              <IconButton
                                onClick={() => handleObtenerCae(factura.nroFormulario)}
                                size="small"
                                disabled={obteniendoCae === factura.nroFormulario}
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': { 
                                    bgcolor: '#1976d2',
                                    transform: 'scale(1.1)',
                                  },
                                  '&:disabled': { bgcolor: '#ccc' },
                                  transition: 'all 0.2s'
                                }}
                              >
                                {obteniendoCae === factura.nroFormulario ? (
                                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                                ) : (
                                  <CloudDownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Anular factura">
                              <IconButton
                                onClick={(e) => handleAnular(factura.nroFormulario, e)}
                                size="small"
                                sx={{
                                  bgcolor: '#f44336',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': { 
                                    bgcolor: '#d32f2f',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Imprimir PDF">
                          <IconButton
                            onClick={() => handleImprimirPDF(factura.nroFormulario)}
                            size="small"
                            disabled={pdfLoading === factura.nroFormulario}
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              width: 32,
                              height: 32,
                              '&:hover': { 
                                bgcolor: '#1976d2',
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s',
                              '&:disabled': { bgcolor: '#ccc' }
                            }}
                          >
                            {pdfLoading === factura.nroFormulario ? (
                              <CircularProgress size={16} sx={{ color: '#fff' }} />
                            ) : (
                              <PrintIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Generar link de pago Mercado Pago">
                          <IconButton
                            onClick={() => handleGenerarLinkPago(factura.nroFormulario)}
                            size="small"
                            sx={{
                              bgcolor: '#4caf50',
                              color: '#fff',
                              borderRadius: 1.5,
                              width: 32,
                              height: 32,
                              '&:hover': { 
                                bgcolor: '#388e3c',
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s'
                            }}
                          >
                            <QrCodeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
              {loading && facturas.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && facturas.length === 0 && (
                <Box sx={{ py: 4 }}>
                  <Typography align="center" color="text.secondary">
                    No hay facturas para mostrar
                  </Typography>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1
                  }
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedFactura && (
        <PDFViewer 
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Factura ${selectedFactura}`}
          documentId={`${selectedFactura}`}
          documentType="factura"
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      {qrDialogOpen && selectedFacturaForPayment && (
        <QRPaymentDialog
          open={qrDialogOpen}
          onClose={handleCloseQRDialog}
          qrCode={qrCode}
          paymentLink={paymentLink}
          facturaNumero={selectedFacturaForPayment}
          loading={qrLoading}
        />
      )}
    </Box>
  );
};

export default Facturas;

